import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { SummaryExampleSetRead } from "@/lib/summaryExamples";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * One Summary version's EFFECTIVE example set, with provenance (#915's read,
 * #916's editor).
 *
 * A SEPARATE READ FROM THE VERSION, for `useAssetSubstitutionsQuery`'s reason
 * and with its shape: the version read returns the stored bytes and must keep
 * doing exactly that — #16's export reproduces a stored identity from it —
 * while this returns the JOIN a client cannot make. The other half of the
 * runtime union is what the pinned base pack ships at the same target, which
 * lives on the deployment's mount and behind `library.read`, and neither the
 * count that half imposes (`inheritedCount`) nor which authored pairs are
 * carried-through inherited ones is derivable in the browser.
 *
 * PER LANGUAGE, because the set is: `<language>/examples` is one part per pack
 * language tree and the endpoint reads one of them. The key carries the
 * language so a prompt stored at two languages caches two answers rather than
 * serving the first one's floor for the second.
 *
 * `enabled` on the version, so a surface that has not resolved a head yet does
 * not fetch a projection of `""`. The only caller is mounted after the
 * `summary_prompt` route dispatch; the service independently refuses every
 * other family with a field-addressed 422 rather than answering an empty set
 * for the wrong asset type.
 */
export function useSummaryExamplesQuery(
  assetId: string,
  version: string | null,
  language: string,
): UseQueryResult<SummaryExampleSetRead> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.summaryExamples(assetId, version ?? "", language),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/library/assets/{assetId}/summary-examples", {
          params: {
            path: { assetId },
            query: { version: version as string, language },
          },
        }),
      );
    },
    enabled: assetId.length > 0 && version !== null && language.length > 0,
  });
}
